import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { MentionList, MentionListRef, MentionUser } from './MentionList';

interface SuggestionProps {
  editor: any;
  clientRect?: (() => DOMRect | null) | null;
  query: string;
  command: (props: { id: string; label: string }) => void;
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

let cachedUsers: MentionUser[] | null = null;
let usersPromise: Promise<MentionUser[]> | null = null;

async function fetchUsers(): Promise<MentionUser[]> {
  if (cachedUsers) return cachedUsers;
  if (usersPromise) return usersPromise;

  usersPromise = axios
    .get(`${API_URL}/api/users`, { headers: authHeaders() })
    .then(res => {
      const users = (res.data || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        profileImage: u.profileImage,
      }));
      cachedUsers = users;
      return users;
    })
    .catch(() => {
      usersPromise = null;
      return [];
    });

  return usersPromise;
}

export const mentionSuggestion = {
  items: async ({ query }: { query: string }): Promise<MentionUser[]> => {
    const users = await fetchUsers();
    const q = query.toLowerCase();
    return users
      .filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8);
  },

  render: () => {
    let component: ReactRenderer<MentionListRef> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: SuggestionProps) => {
        component = new ReactRenderer(MentionList, { props, editor: props.editor });
        if (!props.clientRect) return;

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as any,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'tt-mention',
          arrow: false,
          offset: [0, 4],
        });
      },

      onUpdate: (props: SuggestionProps) => {
        component?.updateProps(props);
        if (!props.clientRect || !popup) return;
        popup[0].setProps({ getReferenceClientRect: props.clientRect as any });
      },

      onKeyDown: (props: { event: KeyboardEvent }) => {
        if (props.event.key === 'Escape') {
          popup?.[0].hide();
          return true;
        }
        return component?.ref?.onKeyDown(props) || false;
      },

      onExit: () => {
        popup?.[0].destroy();
        component?.destroy();
        popup = null;
        component = null;
      },
    };
  },
};
